// What Submit demands before a Plan Order can be sent.
//
// This lives in its own module because it used to exist TWICE: once in
// PlanScreen and once re-implemented inside its test. A rule copied into its
// own test cannot fail — the copy passes while the real one drifts — and this
// rule has now grown three separate exemptions, each from a real advisor being
// blocked by a question they could not answer. One definition, imported by
// both.

import { planSections } from '../schema/planSections.js';

// Everything above the Strategies sections must be answered before Submit.
export const SUBMIT_REQUIRED_SECTIONS = ['documents', 'baseFacts', 'observations', 'taxes'];

// Two field types are never demanded, for different reasons:
//   - checkbox: leaving one unticked IS an answer, so "empty" cannot be told
//     apart from "no".
//   - textarea: free-text commentary (Base Facts Notes, Charitably Inclined).
//     Requiring them would force an advisor with nothing to add to type filler.
export const SUBMIT_EXEMPT_TYPES = ['checkbox', 'textarea'];

// Does this household have a spouse or partner? Same signals the PQ uses: the
// explicit flag, or a second client's name having been entered.
export function householdHasSpouse(data) {
  const fam = data?.family || {};
  return Boolean(
    data?._flags?.hasSpouse ||
    String(fam.client2FirstName || '').trim() ||
    String(fam.client2LastName || '').trim()
  );
}

// An `onlyWhen: { field, in: [...] }` question applies only while the field it
// names holds one of those values. Applicability drives two things — whether
// Submit demands an answer, and whether the control can be used at all — and
// both read the same condition here so they cannot disagree.
//
// `requiredWhen` is the older key and still works: required-when-applicable but
// always fillable. Kept for any field that wants that weaker rule.
function conditionHolds(cond, valueFor) {
  if (!cond) return true;
  const current = String(valueFor({ key: cond.field }) ?? '').trim();
  return cond.in.includes(current);
}

// True when this question applies at all. A field that does not apply is greyed
// out — the advisor cannot answer a question the household's cash flow makes
// meaningless.
export function conditionApplies(field, valueFor) {
  return conditionHolds(field.onlyWhen, valueFor);
}

// True when Submit should demand an answer. Anything else — including the field
// it depends on being unanswered — leaves it optional, which is right: the
// field it depends on is itself required, so the advisor is prompted for the
// cause rather than the consequence.
export function conditionRequires(field, valueFor) {
  return conditionHolds(field.onlyWhen ?? field.requiredWhen, valueFor);
}

// The fields Submit is still waiting on. `valueFor(field)` resolves a field to
// its current answer (stored value, else the smart prefill).
export function getMissingSubmitFields(valueFor, data) {
  const hasSpouse = householdHasSpouse(data);
  return planSections
    .filter(s => SUBMIT_REQUIRED_SECTIONS.includes(s.id))
    .flatMap(s => s.fields)
    .filter(f => !SUBMIT_EXEMPT_TYPES.includes(f.type))
    // Spouse-only fields on a single-client household: Submit was demanding a
    // Spouse Retirement Date for a client with no spouse (Rachel Fuss,
    // 2026-08-10).
    .filter(f => !(f.requiresSpouse && !hasSpouse))
    // Fields the cash-flow answer rules out: a deficit household cannot say
    // where its excess cash goes (Rachel Fuss, 2026-08-18).
    .filter(f => conditionRequires(f, valueFor))
    .filter(f => String(valueFor(f) ?? '').trim() === '')
    // Retirement can be answered as an age OR a date, so either box counts
    // (Kyle Farmer, 2026-09-14). It is still flagged once, by its date key.
    .filter(f => !f.ageKey || String(valueFor({ key: f.ageKey }) ?? '').trim() === '');
}
